import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { CourseLiveLayout } from './realtime/CourseLiveLayout';
import { CourseLiveSkeleton } from './realtime/CourseLiveSkeleton';
import { AccountLimitReached } from './realtime/AccountLimitReached';
import { useChatSocket } from '../../hooks/useChatSocket';
import { useTaskUpdates } from '../../hooks/useTaskUpdates';
import { fetchChatRealtime, fetchCourseEntry, fetchUnreadCounts } from '../../redux/actions/chatRealtime';
import { getCourseById, getListStudentsByIdCourse } from '../../redux/actions/course';
import { resetChatRealtime } from '../../redux/reducers/chatRealtime';
import { resetZoom } from '../../redux/reducers/zoom';
import { resetTasks } from '../../redux/reducers/tasks';
import { requestNotificationPermission } from '../../services/firebase';
import { useFcmRegistration } from '../../hooks/useFcmRegistration';
import { connectSocket, leaveCourseRoom, onSocketEvent } from '../../services/socket';
import { COURSE_ENTRY_REASONS, isStudentCourseEntryPaid } from '../../constants/courseEntry';
import './realtime/course-live-layout.css';

const COURSE_STATUS_AVOID = ['completado', 'cancelado'];

export const CourseDisplayRealTime = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { idCourse } = useParams();
    const [searchParams] = useSearchParams();
    const deepLinkTab = searchParams.get('tab');
    const openTaskId = searchParams.get('task');
    const { user } = useSelector((state) => state.user);
    const { courseSelected, loading: courseLoading } = useSelector((state) => state.course);
    const { loading, chat } = useSelector((state) => state.chatRealtime);
    const clientUnpaid = !isStudentCourseEntryPaid(user);
    const [entryState, setEntryState] = useState(clientUnpaid ? COURSE_ENTRY_REASONS.UNPAID : 'checking');

    const canEnterCourse = entryState === 'ok';
    const isCourseReady = Boolean(courseSelected?._id);
    const isChatReady = Boolean(chat);
    const showSkeleton = canEnterCourse && (
        (loading && !isChatReady) || (courseLoading && !isCourseReady)
    );

    useChatSocket(canEnterCourse ? idCourse : null, user);
    useTaskUpdates(canEnterCourse ? idCourse : null);
    useFcmRegistration(user);

    useEffect(() => {
        if (clientUnpaid) {
            navigate('/cursos', { replace: true });
        }
    }, [clientUnpaid, navigate]);

    useEffect(() => {
        if (!idCourse || clientUnpaid) return undefined;

        let cancelled = false;

        const checkEntry = async () => {
            try {
                try {
                    await connectSocket();
                } catch {
                    // La validación HTTP sigue, con o sin socketId.
                }
                if (cancelled) return;
                const data = await dispatch(fetchCourseEntry(idCourse));
                if (cancelled) return;

                if (data?.reason === COURSE_ENTRY_REASONS.UNPAID) {
                    setEntryState(COURSE_ENTRY_REASONS.UNPAID);
                    navigate('/cursos', { replace: true });
                    return;
                }
                if (data?.reason === COURSE_ENTRY_REASONS.ACCOUNT_LIMIT) {
                    setEntryState(COURSE_ENTRY_REASONS.ACCOUNT_LIMIT);
                    return;
                }
                setEntryState('ok');
            } catch {
                if (!cancelled) setEntryState('ok');
            }
        };

        checkEntry();
        return () => {
            cancelled = true;
        };
    }, [idCourse, clientUnpaid, dispatch, navigate]);

    useEffect(() => {
        if (entryState !== 'ok' || !idCourse) return undefined;

        let mounted = true;
        let unbind = () => {};

        connectSocket()
            .then(() => {
                if (!mounted) return;
                unbind = onSocketEvent('account_limit_reached', (payload) => {
                    if (String(payload?.courseId) !== String(idCourse)) return;
                    leaveCourseRoom(idCourse);
                    setEntryState(COURSE_ENTRY_REASONS.ACCOUNT_LIMIT);
                });
            })
            .catch(() => {});

        return () => {
            mounted = false;
            unbind();
        };
    }, [entryState, idCourse]);

    useEffect(() => {
        if (COURSE_STATUS_AVOID.includes(courseSelected?.status)) {
            navigate('/cursos');
        }
    }, [courseSelected, navigate]);

    useEffect(() => {
        if (!idCourse || !canEnterCourse) return undefined;

        dispatch(getCourseById(idCourse));
        dispatch(getListStudentsByIdCourse(idCourse));
        dispatch(fetchChatRealtime(idCourse)).catch((error) => {
            const reason = error.response?.data?.reason;
            if (reason === COURSE_ENTRY_REASONS.ACCOUNT_LIMIT) {
                setEntryState(COURSE_ENTRY_REASONS.ACCOUNT_LIMIT);
            }
            if (reason === COURSE_ENTRY_REASONS.UNPAID) {
                navigate('/cursos', { replace: true });
            }
        });
        dispatch(fetchUnreadCounts());
        requestNotificationPermission();

        return () => {
            dispatch(resetChatRealtime());
            dispatch(resetZoom());
            dispatch(resetTasks());
        };
    }, [idCourse, canEnterCourse, dispatch, navigate]);

    return (
        <div className="course-live-viewport">
            {entryState === COURSE_ENTRY_REASONS.ACCOUNT_LIMIT ? (
                <AccountLimitReached />
            ) : showSkeleton || entryState === 'checking' || entryState === COURSE_ENTRY_REASONS.UNPAID ? (
                <CourseLiveSkeleton />
            ) : (
                <CourseLiveLayout
                    courseId={idCourse}
                    deepLinkTab={deepLinkTab}
                    openTaskId={openTaskId}
                    onAccountLimit={() => setEntryState(COURSE_ENTRY_REASONS.ACCOUNT_LIMIT)}
                />
            )}
        </div>
    );
};
